import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Row,
  Col,
  Table,
  Tag,
  Popconfirm,
  Checkbox,
  List,
  Avatar,
  Divider,
  Drawer
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  UnorderedListOutlined,
  HolderOutlined,
  UpOutlined,
  DownOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSimpleStore } from '../stores/simpleStore';
import { getApiUrl } from '../utils/api';
import { config } from '../config';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface MediaFile {
  id: number;
  title: string;
  description?: string;
  filename: string;
  original_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  duration?: number;
  thumbnail_path?: string;
  created_at: string;
}

interface PlaylistVersion {
  id: number;
  version: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  created_by_name?: string;
  media_count: number;
  total_duration: number;
}

interface Playlist {
  id: number;
  name: string;
  playlist_name?: string;
  description?: string;
  route_id?: number;
  route_number?: string;
  route_name?: string;
  company_id: number;
  media_count: number;
  total_duration?: number;
  is_active: boolean;
  active_version?: number;
  created_at: string;
}

interface PlaylistMedia {
  id: number;
  title: string;
  filename: string;
  file_path: string;
  mime_type: string;
  duration?: number;
  thumbnail_path?: string;
  play_order: number;
}

interface Route {
  id: number;
  route_number: string;
  route_name: string;
  city_code: string;
  city_name: string;
}

interface Company {
  id: number;
  name: string;
  code: string;
}

export default function PlaylistManagement() {
  const { user, selectedCompanyId, selectedCompanyName, setSelectedCompany } = useSimpleStore();
  const { isMobile } = useResponsive();
  
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 모달 상태
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState<Playlist | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [versionsModalVisible, setVersionsModalVisible] = useState(false);
  const [playlistVersions, setPlaylistVersions] = useState<PlaylistVersion[]>([]);
  const [versioningPlaylist, setVersioningPlaylist] = useState<Playlist | null>(null);
  const [viewingPlaylist, setViewingPlaylist] = useState<Playlist | null>(null);
  const [playlistMedia, setPlaylistMedia] = useState<PlaylistMedia[]>([]);
  
  // 미리보기 재생 상태
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [currentMediaElement, setCurrentMediaElement] = useState<HTMLVideoElement | HTMLAudioElement | null>(null);
  const [currentMediaUrl, setCurrentMediaUrl] = useState<string | null>(null);
  const [currentMediaType, setCurrentMediaType] = useState<string>('');
  
  // 폼과 미디어 선택 상태
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedMediaIds, setSelectedMediaIds] = useState<number[]>([]);

  useEffect(() => {
    console.log('사용자 정보 변경:', user, 'selectedCompanyId:', selectedCompanyId);
    if (user?.role === 'admin') {
      console.log('관리자로 인식, 회사 목록 로드 시작');
      loadCompanies();
    }
  }, [user]);

  useEffect(() => {
    if (selectedCompanyId) {
      loadRoutes();
      loadPlaylists();
      loadMediaFiles();
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      console.log('회사 목록 로드 시작, 토큰:', token);
      
      const response = await fetch(getApiUrl('/api/companies'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      console.log('회사 목록 응답:', data);
      
      if (data.success) {
        setCompanies(data.data || []);
        console.log('회사 목록 설정:', data.data);
        if (data.data && data.data.length > 0 && !selectedCompanyId) {
          setSelectedCompany(data.data[0].id, data.data[0].name);
          console.log('기본 회사 선택:', data.data[0].id, data.data[0].name);
        }
      }
    } catch (error) {
      console.error('회사 목록 로드 실패:', error);
    }
  };

  const loadRoutes = async () => {
    if (!selectedCompanyId) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/company-routes/list?company_id=${selectedCompanyId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setRoutes(data.data || []);
      }
    } catch (error) {
      console.error('노선 목록 로드 실패:', error);
    }
  };

  const loadPlaylists = async () => {
    if (!selectedCompanyId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/route-playlists/list?company_id=${selectedCompanyId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setPlaylists(data.data || []);
        console.log('노선 플레이리스트 로드 완료:', data.data);
      }
    } catch (error) {
      console.error('노선 플레이리스트 로드 실패:', error);
      message.error('노선 플레이리스트를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  const loadMediaFiles = async () => {
    if (!selectedCompanyId) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      // 시스템 영상 포함해서 로드
      const url = getApiUrl(`/api/media/list?company_id=${selectedCompanyId}&include_system=true&limit=1000`);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setMediaFiles(data.data || []);
      }
    } catch (error) {
      console.error('미디어 파일 로드 실패:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleCreate = async (values: any) => {
    if (!selectedCompanyId) {
      message.error('회사를 선택해주세요');
      return;
    }

    if (!values.route_id) {
      message.error('노선을 선택해주세요');
      return;
    }

    if (selectedMediaIds.length === 0) {
      message.error('플레이리스트에 포함할 미디어를 선택해주세요');
      return;
    }

    // 선택된 노선 정보 찾기
    const selectedRoute = routes.find(r => r.id === values.route_id);
    if (!selectedRoute) {
      message.error('선택된 노선 정보를 찾을 수 없습니다');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl('/api/route-playlists'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          company_id: selectedCompanyId,
          route_number: selectedRoute.route_number,
          name: `${selectedRoute.route_number} - ${selectedRoute.route_name}`,
          description: values.description || `${selectedRoute.route_number}번 노선 플레이리스트`,
          media_ids: selectedMediaIds
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success(`${selectedRoute.route_number}번 노선 플레이리스트가 생성되었습니다`);
        setCreateModalVisible(false);
        createForm.resetFields();
        setSelectedMediaIds([]);
        loadPlaylists();
      } else {
        message.error(data.error || '노선 플레이리스트 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('노선 플레이리스트 생성 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  const handleEdit = async (playlist: Playlist) => {
    setEditingPlaylist(playlist);
    editForm.setFieldsValue({
      route_id: playlist.route_id,
      description: `현재 활성 버전 v${playlist.active_version || 1} 편집`
    });

    // 현재 활성 버전의 미디어 목록을 로드
    loadPlaylistMedia(playlist.id);
    setEditModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    if (!editingPlaylist) return;

    if (selectedMediaIds.length === 0) {
      message.error('새 버전에 포함할 미디어를 선택해주세요');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/route-playlists/${editingPlaylist.id}/versions`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: values.description || `v${(editingPlaylist.active_version || 1) + 1}`,
          description: values.description || `v${(editingPlaylist.active_version || 1) + 1}`,
          media_ids: selectedMediaIds
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success(`${editingPlaylist.route_number}번 노선의 새 버전이 생성되었습니다`);
        setEditModalVisible(false);
        editForm.resetFields();
        setSelectedMediaIds([]);
        setEditingPlaylist(null);
        loadPlaylists();
      } else {
        message.error(data.error || '새 버전 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('새 버전 생성 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/route-playlists/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        message.success('노선 플레이리스트가 삭제되었습니다');
        loadPlaylists();
      } else {
        message.error(data.error || '노선 플레이리스트 삭제에 실패했습니다');
      }
    } catch (error) {
      console.error('노선 플레이리스트 삭제 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  // 새 버전 생성
  const handleCreateVersion = (playlist: Playlist) => {
    setEditingPlaylist(playlist);
    editForm.setFieldsValue({
      route_id: playlist.route_id,
      description: `v${(playlist.active_version || 1) + 1} - ${new Date().toLocaleDateString()}`
    });
    
    // 기존 버전의 미디어 목록을 로드해서 기본값으로 설정
    loadPlaylistMedia(playlist.id);
    setEditModalVisible(true);
  };

  // 버전 이력 보기
  const handleViewVersions = async (playlist: Playlist) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/route-playlists/${playlist.id}/versions`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setPlaylistVersions(data.data || []);
        setVersioningPlaylist(playlist);
        setVersionsModalVisible(true);
      } else {
        message.error(data.error || '버전 이력을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('버전 이력 조회 실패:', error);
      message.error('버전 이력을 불러올 수 없습니다');
    }
  };

  // 버전 활성화
  const handleActivateVersion = async (version: number) => {
    if (!versioningPlaylist) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/route-playlists/${versioningPlaylist.id}/versions/${version}/activate`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        message.success(`버전 ${version}이 활성화되었습니다`);
        // 버전 목록 새로고침
        handleViewVersions(versioningPlaylist);
        // 플레이리스트 목록 새로고침
        loadPlaylists();
      } else {
        message.error(data.error || '버전 활성화에 실패했습니다');
      }
    } catch (error) {
      console.error('버전 활성화 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  // 플레이리스트 미디어 로드 (편집용)
  const loadPlaylistMedia = async (playlistId: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/route-playlists/${playlistId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success && data.data.media) {
        setSelectedMediaIds(data.data.media.map((m: any) => m.id));
      }
    } catch (error) {
      console.error('플레이리스트 미디어 로드 실패:', error);
    }
  };

  // 플레이리스트 상세 내용 조회
  const handleViewPlaylist = async (playlist: Playlist) => {
    console.log('handleViewPlaylist 호출됨:', playlist);
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const url = getApiUrl(`/api/route-playlists/${playlist.id}/versions/${playlist.active_version || 1}`);
      console.log('API 호출 URL:', url);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('API 응답 상태:', response.status);
      const data = await response.json();
      console.log('API 응답 데이터:', data);
      
      if (data.success) {
        console.log('플레이리스트 데이터 설정 중...');
        setViewingPlaylist(playlist);
        const mediaList = data.data.media || [];
        console.log('미디어 목록 (정렬 전):', mediaList);
        
        // play_order로 정렬 (백엔드에서 정렬이 안 된 경우 대비)
        const sortedMediaList = mediaList.sort((a, b) => (a.play_order || 0) - (b.play_order || 0));
        console.log('미디어 목록 (play_order 정렬 후):', sortedMediaList);
        
        setPlaylistMedia(sortedMediaList);
        setCurrentPreviewIndex(0);
        setPreviewPlaying(false);
        
        // 첫 번째 미디어 정보 미리 설정 (재생은 하지 않음)
        if (mediaList.length > 0) {
          const firstMedia = mediaList[0];
          const mediaUrl = `${config.mediaBaseUrl}/${firstMedia.file_path}`;
          console.log('첫 번째 미디어 URL:', mediaUrl);
          setCurrentMediaUrl(mediaUrl);
          setCurrentMediaType(firstMedia.mime_type);
        }
        
        console.log('모달 열기 시도...');
        setViewModalVisible(true);
      } else {
        console.error('API 실패:', data.error);
        message.error(data.error || '플레이리스트 내용을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('플레이리스트 조회 실패:', error);
      message.error('서버 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 미리보기 재생 제어
  const handlePreviewPlay = () => {
    console.log('handlePreviewPlay 시작 - 현재 상태:', { previewPlaying, currentPreviewIndex, mediaCount: playlistMedia.length });
    
    if (!playlistMedia.length) {
      console.log('미디어가 없어 재생할 수 없습니다');
      return;
    }

    const currentMedia = playlistMedia[currentPreviewIndex];
    if (!currentMedia) {
      console.log('현재 미디어를 찾을 수 없습니다');
      return;
    }

    if (previewPlaying) {
      // 정지
      console.log('재생 정지');
      if (currentMediaElement) {
        currentMediaElement.pause();
      }
      setPreviewPlaying(false);
    } else {
      // 재생
      console.log('재생 시작');
      setPreviewPlaying(true);
      // useEffect 대신 직접 재생
      setTimeout(() => playCurrentMedia(), 100);
    }
  };

  const playCurrentMedia = () => {
    console.log('playCurrentMedia 시작 - 인덱스:', currentPreviewIndex);
    
    if (!playlistMedia.length) {
      console.log('미디어 목록이 비어있음');
      return;
    }

    const currentMedia = playlistMedia[currentPreviewIndex];
    if (!currentMedia) {
      console.log('현재 인덱스의 미디어를 찾을 수 없음');
      return;
    }

    console.log('재생할 미디어:', currentMedia);

    // 기존 재생 중인 미디어 정지
    if (currentMediaElement) {
      console.log('기존 미디어 정지');
      currentMediaElement.pause();
      currentMediaElement.removeEventListener('ended', handleMediaEnded);
    }

    // 미디어 URL 및 타입 설정
    const mediaUrl = `${config.mediaBaseUrl}/${currentMedia.file_path}`;
    console.log('미디어 URL:', mediaUrl);
    console.log('미디어 타입:', currentMedia.mime_type);

    setCurrentMediaUrl(mediaUrl);
    setCurrentMediaType(currentMedia.mime_type);

    // mime_type 정규화 (application/mp4 등을 video/mp4로 변환)
    let normalizedMimeType = currentMedia.mime_type;
    if (normalizedMimeType === 'application/mp4' || 
        currentMedia.filename.toLowerCase().endsWith('.mp4') ||
        currentMedia.filename.toLowerCase().endsWith('.webm') ||
        currentMedia.filename.toLowerCase().endsWith('.avi') ||
        currentMedia.filename.toLowerCase().endsWith('.mov')) {
      normalizedMimeType = 'video/mp4';
    }
    
    if (normalizedMimeType.startsWith('video/') || 
        normalizedMimeType === 'application/mp4') {
      console.log('비디오 재생 시도');
      // DOM이 업데이트될 시간을 주기 위해 setTimeout 사용
      setTimeout(() => {
        const videoElement = document.getElementById('preview-video') as HTMLVideoElement;
        console.log('비디오 엘리먼트:', videoElement);
        
        if (videoElement) {
          videoElement.src = mediaUrl;
          videoElement.addEventListener('ended', handleMediaEnded);
          videoElement.addEventListener('error', (e) => {
            console.error('비디오 재생 오류:', e);
            message.error(`${currentMedia.title} 재생 중 오류가 발생했습니다`);
            handleNextMedia();
          });
          setCurrentMediaElement(videoElement);
          
          // play() 호출
          const playPromise = videoElement.play();
          console.log('play() 호출됨');
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('비디오 재생 시작됨');
              })
              .catch(e => {
                console.error('비디오 재생 실패:', e);
                message.error('비디오 재생에 실패했습니다');
                setPreviewPlaying(false);
              });
          }
        } else {
          console.error('비디오 엘리먼트를 찾을 수 없음');
        }
      }, 200);
    } else if (normalizedMimeType.startsWith('audio/') ||
               currentMedia.filename.toLowerCase().endsWith('.mp3') ||
               currentMedia.filename.toLowerCase().endsWith('.wav')) {
      console.log('오디오 재생 시도');
      setTimeout(() => {
        const audioElement = document.getElementById('preview-audio') as HTMLAudioElement;
        console.log('오디오 엘리먼트:', audioElement);
        
        if (audioElement) {
          audioElement.src = mediaUrl;
          audioElement.addEventListener('ended', handleMediaEnded);
          audioElement.addEventListener('error', (e) => {
            console.error('오디오 재생 오류:', e);
            message.error(`${currentMedia.title} 재생 중 오류가 발생했습니다`);
            handleNextMedia();
          });
          setCurrentMediaElement(audioElement);
          
          const playPromise = audioElement.play();
          console.log('오디오 play() 호출됨');
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('오디오 재생 시작됨');
              })
              .catch(e => {
                console.error('오디오 재생 실패:', e);
                message.error('오디오 재생에 실패했습니다');
                setPreviewPlaying(false);
              });
          }
        } else {
          console.error('오디오 엘리먼트를 찾을 수 없음');
        }
      }, 200);
    } else if (normalizedMimeType.startsWith('image/') ||
               currentMedia.filename.toLowerCase().endsWith('.jpg') ||
               currentMedia.filename.toLowerCase().endsWith('.jpeg') ||
               currentMedia.filename.toLowerCase().endsWith('.png') ||
               currentMedia.filename.toLowerCase().endsWith('.gif')) {
      console.log('이미지 표시 - 자동 넘김 타이머 설정');
      // 이미지는 자동으로 다음으로 넘어가기
      const duration = currentMedia.duration ? currentMedia.duration * 1000 : 5000;
      console.log(`${duration}ms 후에 다음 미디어로 넘어갑니다`);
      setTimeout(() => {
        if (previewPlaying) {
          handleNextMedia();
        }
      }, duration);
    } else {
      console.warn('알 수 없는 미디어 타입:', currentMedia.mime_type);
      // 알 수 없는 타입도 일단 이미지처럼 처리
      const duration = currentMedia.duration ? currentMedia.duration * 1000 : 5000;
      setTimeout(() => {
        if (previewPlaying) {
          handleNextMedia();
        }
      }, duration);
    }
  };

  const handleMediaEnded = () => {
    handleNextMedia();
  };

  const handleNextMedia = () => {
    console.log('handleNextMedia 호출됨');
    let nextIndex;
    
    if (currentPreviewIndex < playlistMedia.length - 1) {
      nextIndex = currentPreviewIndex + 1;
    } else {
      // 마지막 미디어면 처음으로 돌아가고 재생 정지
      nextIndex = 0;
      setPreviewPlaying(false);
      console.log('플레이리스트 끝 - 재생 정지');
    }
    
    console.log('다음 인덱스로 이동:', nextIndex);
    
    // 인덱스만 변경 - useEffect에서 자동 재생 처리
    setCurrentPreviewIndex(nextIndex);
  };

  const handlePrevMedia = () => {
    let prevIndex;
    if (currentPreviewIndex > 0) {
      prevIndex = currentPreviewIndex - 1;
    } else {
      prevIndex = playlistMedia.length - 1;
    }
    
    console.log('이전 인덱스로 이동:', prevIndex);
    
    // 인덱스만 변경 - useEffect에서 자동 재생 처리
    setCurrentPreviewIndex(prevIndex);
  };

  // 미디어 직접 선택
  const handleSelectMedia = (index: number) => {
    console.log('미디어 직접 선택:', index);
    
    // 인덱스만 변경 - useEffect에서 자동 재생 처리
    setCurrentPreviewIndex(index);
  };

  // useEffect로 현재 인덱스 변경 시 미디어 업데이트 및 자동 재생
  useEffect(() => {
    console.log('useEffect - currentPreviewIndex 변경:', currentPreviewIndex, 'previewPlaying:', previewPlaying);
    
    // 플레이리스트가 있고 유효한 인덱스일 때만
    if (playlistMedia.length > 0 && currentPreviewIndex >= 0 && currentPreviewIndex < playlistMedia.length) {
      const currentMedia = playlistMedia[currentPreviewIndex];
      const mediaUrl = `${config.mediaBaseUrl}/${currentMedia.file_path}`;

      // 현재 표시할 미디어 URL 업데이트
      console.log('현재 미디어 URL 업데이트:', mediaUrl);
      setCurrentMediaUrl(mediaUrl);
      setCurrentMediaType(currentMedia.mime_type);
      
      // 재생 중이면 새 미디어 자동 재생
      if (previewPlaying && viewModalVisible) {
        console.log('자동 재생 시작');
        
        // MIME 타입 정규화
        let normalizedMimeType = currentMedia.mime_type;
        if (normalizedMimeType === 'application/mp4' || 
            currentMedia.filename.toLowerCase().endsWith('.mp4')) {
          normalizedMimeType = 'video/mp4';
        }
        
        // DOM이 업데이트된 후 재생
        setTimeout(() => {
          if (normalizedMimeType.startsWith('video/') || normalizedMimeType === 'application/mp4') {
            const videoElement = document.getElementById('preview-video') as HTMLVideoElement;
            if (videoElement) {
              console.log('비디오 엘리먼트 자동 재생');
              videoElement.play().then(() => {
                console.log('비디오 자동 재생 성공');
              }).catch(e => {
                console.error('비디오 자동 재생 실패:', e);
              });
            }
          } else if (normalizedMimeType.startsWith('audio/')) {
            const audioElement = document.getElementById('preview-audio') as HTMLAudioElement;
            if (audioElement) {
              console.log('오디오 엘리먼트 자동 재생');
              audioElement.play().then(() => {
                console.log('오디오 자동 재생 성공');
              }).catch(e => {
                console.error('오디오 자동 재생 실패:', e);
              });
            }
          } else if (normalizedMimeType.startsWith('image/') || 
                     currentMedia.filename.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)) {
            // 이미지는 타이머로 자동 넘김
            console.log('이미지 자동 넘김 타이머 설정');
            const duration = currentMedia.duration ? currentMedia.duration * 1000 : 5000;
            const timer = setTimeout(() => {
              if (previewPlaying) {
                handleNextMedia();
              }
            }, duration);
            
            // 클린업 함수에서 타이머 제거
            return () => clearTimeout(timer);
          }
        }, 500); // DOM 업데이트를 위해 충분한 시간 대기
      }
    }
  }, [currentPreviewIndex, playlistMedia, previewPlaying, viewModalVisible]);

  // 모달 닫을 때 재생 정지
  const handleCloseViewModal = () => {
    if (currentMediaElement) {
      currentMediaElement.pause();
      currentMediaElement.removeEventListener('ended', handleMediaEnded);
    }
    setCurrentMediaElement(null);
    setPreviewPlaying(false);
    setCurrentMediaUrl(null);
    setCurrentMediaType('');
    setCurrentPreviewIndex(0);
    setViewModalVisible(false);
    setViewingPlaylist(null);
    setPlaylistMedia([]);
  };

  const columns = [
    {
      title: '노선 정보',
      dataIndex: 'route_number',
      key: 'route_info',
      render: (route_number: string, record: Playlist) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {record.route_number} - {record.route_name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              현재 버전: v{record.active_version || 1}
            </Text>
            {record.description && (
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                • {record.description}
              </Text>
            )}
          </div>
        </div>
      )
    },
    {
      title: '미디어 정보',
      key: 'media_info',
      width: 150,
      render: (_, record: Playlist) => (
        <div>
          <Tag color="green">{record.media_count || 0}개 미디어</Tag>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.total_duration ? (
                `${Math.floor(record.total_duration / 60)}:${(record.total_duration % 60).toString().padStart(2, '0')}`
              ) : '0:00'}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: '버전 관리',
      key: 'version',
      width: 120,
      render: (_, record: Playlist) => (
        <div>
          <Button
            type="primary"
            size="small"
            style={{ marginBottom: 4, width: '100%' }}
            onClick={() => handleCreateVersion(record)}
          >
            새 버전 생성
          </Button>
          <Button
            type="default"
            size="small"
            style={{ width: '100%' }}
            onClick={() => handleViewVersions(record)}
          >
            버전 이력
          </Button>
        </div>
      )
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date: string) => new Date(date).toLocaleDateString('ko-KR')
    },
    {
      title: '작업',
      key: 'action',
      width: 120,
      render: (_, record: Playlist) => (
        <Space direction="vertical">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewPlaylist(record)}
            style={{ width: '100%' }}
          >
            상세보기
          </Button>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ width: '100%' }}
          >
            편집
          </Button>
          <Popconfirm
            title="이 노선 플레이리스트를 삭제하시겠습니까?"
            onConfirm={() => handleDelete(record.id)}
            okText="삭제"
            cancelText="취소"
          >
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ width: '100%' }}
            >
              삭제
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Title level={2} style={{ margin: 0 }}>
              <UnorderedListOutlined /> 플레이리스트 관리
            </Title>
            <div style={{ fontSize: '14px', color: '#666' }}>
              📢 선택된 운수사: <strong>{selectedCompanyName || '선택되지 않음'}</strong>
            </div>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCreateModalVisible(true);
            }}
            disabled={!selectedCompanyId}
          >
            노선 플레이리스트 생성
          </Button>
        </div>
      </div>


      {/* 플레이리스트 테이블 */}
      <Card>
        <Table
          columns={columns}
          dataSource={playlists}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}개`
          }}
        />
      </Card>

      {/* 노선 플레이리스트 생성 모달 */}
      <Modal
        title="🚌 노선 플레이리스트 생성"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
          setSelectedMediaIds([]);
        }}
        footer={null}
        width={800}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item 
                name="route_id" 
                label="노선 선택"
                rules={[{ required: true, message: '노선을 선택해주세요' }]}
              >
                <Select
                  placeholder="노선을 선택해주세요 - 플레이리스트 이름이 됩니다"
                  showSearch
                  disabled={!selectedCompanyId}
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                  {routes.map(route => (
                    <Option key={route.id} value={route.id}>
                      {route.route_number} - {route.route_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="설명 (선택사항)">
            <TextArea rows={2} placeholder="플레이리스트 설명" />
          </Form.Item>

          <Form.Item label="포함할 미디어 선택" required>
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: 6, 
              padding: 16, 
              maxHeight: 300, 
              overflowY: 'auto' 
            }}>
              {mediaFiles.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
                  업로드된 미디어가 없습니다
                </div>
              ) : (
                <List
                  dataSource={mediaFiles}
                  renderItem={(media) => (
                    <List.Item style={{ padding: '8px 0', border: 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Checkbox
                          checked={selectedMediaIds.includes(media.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMediaIds([...selectedMediaIds, media.id]);
                            } else {
                              setSelectedMediaIds(selectedMediaIds.filter(id => id !== media.id));
                            }
                          }}
                          style={{ marginRight: 12 }}
                        />
                        
                        <Avatar 
                          shape="square" 
                          size={40} 
                          src={media.thumbnail_path ? `${config.mediaBaseUrl}/${media.thumbnail_path}` : null}
                          icon={media.mime_type?.startsWith('image/') ? <FileImageOutlined /> : <VideoCameraOutlined />}
                          style={{ marginRight: 12 }}
                        />
                        
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500 }}>{media.title}</div>
                          <div style={{ fontSize: 12, color: '#999' }}>
                            {media.original_name} • {formatFileSize(media.file_size)}
                            {media.duration && ` • ${Math.floor(media.duration / 60)}:${(media.duration % 60).toString().padStart(2, '0')}`}
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              )}
              
              {selectedMediaIds.length > 0 && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <div style={{ color: '#1890ff', fontSize: 14 }}>
                    선택된 미디어: {selectedMediaIds.length}개
                  </div>
                </>
              )}
            </div>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
                setSelectedMediaIds([]);
              }}>
                취소
              </Button>
              <Button type="primary" htmlType="submit">
                생성
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 새 버전 생성 모달 */}
      <Modal
        title={editingPlaylist ? `${editingPlaylist.route_number}번 노선 - 새 버전 생성` : "버전 생성"}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setSelectedMediaIds([]);
          setEditingPlaylist(null);
        }}
        footer={null}
        width={800}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item 
                name="route_id" 
                label="노선 정보 (변경 불가)"
              >
                <Select
                  disabled
                  value={editingPlaylist?.route_id}
                >
                  <Option value={editingPlaylist?.route_id}>
                    {editingPlaylist?.route_number} - {editingPlaylist?.route_name}
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="설명 (선택사항)">
            <TextArea rows={2} placeholder="플레이리스트 설명" />
          </Form.Item>

          <Form.Item label="포함할 미디어 선택" required>
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: 6, 
              padding: 16, 
              maxHeight: 300, 
              overflowY: 'auto' 
            }}>
              {mediaFiles.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
                  업로드된 미디어가 없습니다
                </div>
              ) : (
                <List
                  dataSource={mediaFiles}
                  renderItem={(media) => (
                    <List.Item style={{ padding: '8px 0', border: 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Checkbox
                          checked={selectedMediaIds.includes(media.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMediaIds([...selectedMediaIds, media.id]);
                            } else {
                              setSelectedMediaIds(selectedMediaIds.filter(id => id !== media.id));
                            }
                          }}
                          style={{ marginRight: 12 }}
                        />
                        
                        <Avatar 
                          shape="square" 
                          size={40} 
                          src={media.thumbnail_path ? `${config.mediaBaseUrl}/${media.thumbnail_path}` : null}
                          icon={media.mime_type?.startsWith('image/') ? <FileImageOutlined /> : <VideoCameraOutlined />}
                          style={{ marginRight: 12 }}
                        />
                        
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500 }}>{media.title}</div>
                          <div style={{ fontSize: 12, color: '#999' }}>
                            {media.original_name} • {formatFileSize(media.file_size)}
                            {media.duration && ` • ${Math.floor(media.duration / 60)}:${(media.duration % 60).toString().padStart(2, '0')}`}
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              )}
              
              {selectedMediaIds.length > 0 && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <div style={{ color: '#1890ff', fontSize: 14 }}>
                    선택된 미디어: {selectedMediaIds.length}개
                  </div>
                </>
              )}
            </div>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setSelectedMediaIds([]);
                setEditingPlaylist(null);
              }}>
                취소
              </Button>
              <Button type="primary" htmlType="submit">
                새 버전 생성
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 플레이리스트 상세 보기 Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <UnorderedListOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {viewingPlaylist?.route_number} - {viewingPlaylist?.playlist_name}
            </span>
            <Tag color="blue">v{viewingPlaylist?.active_version || 1}</Tag>
          </div>
        }
        placement="right"
        width={window.innerWidth > 1400 ? 900 : window.innerWidth > 1000 ? 700 : '90%'}
        open={viewModalVisible}
        onClose={handleCloseViewModal}
        bodyStyle={{ padding: '20px 24px' }}
        style={{ zIndex: 1001 }}
      >
        {viewingPlaylist ? (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* 플레이리스트 정보 */}
              <Card 
                size="small" 
                style={{ 
                  marginBottom: 16,
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>노선:</span>
                      <span style={{ fontWeight: 600 }}>{viewingPlaylist.route_number} - {viewingPlaylist.route_name}</span>
                    </div>
                    <div>
                      <span style={{ color: '#666', marginRight: 8 }}>미디어 개수:</span>
                      <span style={{ fontWeight: 600 }}>{playlistMedia.length}개</span>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>총 재생시간:</span>
                      <span style={{ fontWeight: 600 }}>
                        {Math.floor((playlistMedia.reduce((sum, media) => sum + (media.duration || 0), 0)) / 60)}분
                      </span>
                    </div>
                    <div>
                      <span style={{ color: '#666', marginRight: 8 }}>활성 버전:</span>
                      <span style={{ fontWeight: 600 }}>v{viewingPlaylist.active_version || 1}</span>
                    </div>
                  </Col>
                </Row>
              </Card>

            {/* 미리보기 재생 제어 */}
            {playlistMedia.length > 0 && (
              <Card 
                size="small" 
                style={{ 
                  marginBottom: 16,
                  borderRadius: 8,
                  background: 'linear-gradient(to right, #f0f5ff, #ffffff)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button
                      type="primary"
                      size="middle"
                      icon={previewPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                      onClick={handlePreviewPlay}
                      style={{ minWidth: 120 }}
                    >
                      {previewPlaying ? '일시정지' : '미리보기 재생'}
                    </Button>
                    <Button.Group>
                      <Button
                        icon={<StepBackwardOutlined />}
                        onClick={handlePrevMedia}
                        disabled={playlistMedia.length <= 1}
                      >
                        이전
                      </Button>
                      <Button
                        icon={<StepForwardOutlined />}
                        onClick={handleNextMedia}
                        disabled={playlistMedia.length <= 1}
                      >
                        다음
                      </Button>
                    </Button.Group>
                  </div>
                  <Tag color="blue" style={{ margin: 0, fontSize: 14 }}>
                    {currentPreviewIndex + 1} / {playlistMedia.length}
                  </Tag>
                </div>
                
                {/* 미디어 플레이어 - 미리보기 재생 중일 때만 표시 */}
                {previewPlaying && playlistMedia[currentPreviewIndex] && (
                  <div style={{ 
                    marginTop: 16, 
                    padding: 16, 
                    border: '2px solid #1890ff',
                    borderRadius: 8,
                    backgroundColor: '#fafafa'
                  }}>
                    <div style={{ 
                      fontWeight: 600, 
                      marginBottom: 12, 
                      textAlign: 'center', 
                      color: '#1890ff',
                      fontSize: 15
                    }}>
                      🎬 현재 재생 중: {playlistMedia[currentPreviewIndex].title}
                    </div>
                    
                    
                    {/* 비디오 플레이어 */}
                    {(playlistMedia[currentPreviewIndex].mime_type.startsWith('video/') || 
                      playlistMedia[currentPreviewIndex].mime_type === 'application/mp4' ||
                      playlistMedia[currentPreviewIndex].filename.toLowerCase().endsWith('.mp4')) && (
                      <div style={{ textAlign: 'center', backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                        <p style={{ color: '#1890ff', marginBottom: 12 }}>📹 비디오 플레이어</p>
                        <video
                          key={`video-${currentPreviewIndex}-${playlistMedia[currentPreviewIndex].id}`}
                          id="preview-video"
                          controls
                          src={`${config.mediaBaseUrl}/${playlistMedia[currentPreviewIndex].file_path}`}
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '400px',
                            backgroundColor: '#000',
                            borderRadius: 4,
                            border: '3px solid #1890ff',
                            display: 'block',
                            margin: '0 auto'
                          }}
                          onEnded={() => {
                            console.log('비디오 재생 종료 - 다음으로 이동');
                            if (previewPlaying) {
                              handleNextMedia();
                            }
                          }}
                          onError={(e) => console.error('비디오 로드 오류:', e)}
                          onLoadedData={() => console.log('비디오 로드 완료')}
                          onPlay={() => console.log('비디오 재생 시작')}
                          onPause={() => console.log('비디오 일시정지')}
                        />
                      </div>
                    )}
                    
                    {/* 오디오 플레이어 */}
                    {(playlistMedia[currentPreviewIndex].mime_type.startsWith('audio/') ||
                      playlistMedia[currentPreviewIndex].filename.toLowerCase().endsWith('.mp3') ||
                      playlistMedia[currentPreviewIndex].filename.toLowerCase().endsWith('.wav')) && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: 16, padding: 40, backgroundColor: '#fff', borderRadius: 8, border: '2px dashed #d9d9d9' }}>
                          <VideoCameraOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                          <div style={{ marginTop: 8, color: '#666' }}>🎵 오디오 재생 중</div>
                        </div>
                        <audio
                          key={`audio-${currentPreviewIndex}-${playlistMedia[currentPreviewIndex].id}`}
                          id="preview-audio"
                          controls
                          src={`${config.mediaBaseUrl}/${playlistMedia[currentPreviewIndex].file_path}`}
                          style={{ width: '100%' }}
                          onEnded={() => {
                            console.log('오디오 재생 종료 - 다음으로 이동');
                            if (previewPlaying) {
                              handleNextMedia();
                            }
                          }}
                          onError={(e) => console.error('오디오 로드 오류:', e)}
                          onLoadedData={() => console.log('오디오 로드 완료')}
                          onPlay={() => console.log('오디오 재생 시작')}
                          onPause={() => console.log('오디오 일시정지')}
                        />
                      </div>
                    )}
                    
                    {/* 이미지 표시 */}
                    {(playlistMedia[currentPreviewIndex].mime_type.startsWith('image/') ||
                      playlistMedia[currentPreviewIndex].filename.toLowerCase().endsWith('.jpg') ||
                      playlistMedia[currentPreviewIndex].filename.toLowerCase().endsWith('.jpeg') ||
                      playlistMedia[currentPreviewIndex].filename.toLowerCase().endsWith('.png') ||
                      playlistMedia[currentPreviewIndex].filename.toLowerCase().endsWith('.gif')) && (
                      <div style={{ textAlign: 'center', backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                        <p style={{ color: '#1890ff', marginBottom: 12 }}>🖼️ 이미지 표시</p>
                        <img
                          src={`${config.mediaBaseUrl}/${playlistMedia[currentPreviewIndex].file_path}`}
                          alt={playlistMedia[currentPreviewIndex].title}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            borderRadius: 4,
                            objectFit: 'contain',
                            border: '3px solid #1890ff'
                          }}
                          onError={(e) => console.error('이미지 로드 오류:', e)}
                          onLoad={() => console.log('이미지 로드 완료')}
                        />
                        <div style={{ marginTop: 8, color: '#1890ff', fontSize: 12 }}>
                          📷 이미지가 {playlistMedia[currentPreviewIndex].duration || 5}초 후 자동으로 넘어갑니다
                        </div>
                      </div>
                    )}
                    
                    <div style={{ fontSize: 12, color: '#666', textAlign: 'center', marginTop: 8 }}>
                      {playlistMedia[currentPreviewIndex].mime_type}
                      {playlistMedia[currentPreviewIndex].duration && 
                        ` • ${Math.floor(playlistMedia[currentPreviewIndex].duration! / 60)}:${(playlistMedia[currentPreviewIndex].duration! % 60).toString().padStart(2, '0')}`
                      }
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* 미디어 목록 */}
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <UnorderedListOutlined />
                  <span>플레이리스트 내용</span>
                  <Tag color="green">{playlistMedia.length}개</Tag>
                </div>
              }
              size="small"
              style={{ 
                flex: 1,
                overflow: 'auto',
                borderRadius: 8
              }}
              bodyStyle={{ 
                maxHeight: 'calc(100vh - 480px)',
                overflow: 'auto'
              }}
            >
              {playlistMedia.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                  이 플레이리스트에는 미디어가 없습니다
                </div>
              ) : (
                <List
                  dataSource={playlistMedia}
                  renderItem={(media, index) => (
                    <List.Item 
                      style={{ 
                        padding: '12px 0', 
                        border: 'none',
                        backgroundColor: index === currentPreviewIndex ? '#e6f7ff' : 'transparent',
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSelectMedia(index)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <div style={{ 
                          marginRight: 12, 
                          fontWeight: 'bold', 
                          minWidth: 24, 
                          textAlign: 'center',
                          color: index === currentPreviewIndex ? '#1890ff' : '#999'
                        }}>
                          {media.play_order}
                        </div>
                        
                        <Avatar 
                          shape="square" 
                          size={50} 
                          src={media.thumbnail_path ? `${config.mediaBaseUrl}/${media.thumbnail_path}` : null}
                          icon={media.mime_type?.startsWith('image/') ? <FileImageOutlined /> : <VideoCameraOutlined />}
                          style={{ marginRight: 12 }}
                        />
                        
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: 500,
                            color: index === currentPreviewIndex ? '#1890ff' : 'inherit'
                          }}>
                            {media.title}
                          </div>
                          <div style={{ fontSize: 12, color: '#999' }}>
                            {media.filename}
                            {media.duration && ` • ${Math.floor(media.duration / 60)}:${(media.duration % 60).toString().padStart(2, '0')}`}
                          </div>
                        </div>

                        {index === currentPreviewIndex && previewPlaying && (
                          <div style={{ marginLeft: 12 }}>
                            <Tag color="processing">재생 중</Tag>
                          </div>
                        )}
                      </div>
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </div>
        ) : (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <p>플레이리스트 정보를 불러오는 중...</p>
          </div>
        )}
      </Drawer>

      {/* 버전 이력 모달 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HistoryOutlined />
            {versioningPlaylist && `${versioningPlaylist.route_number}번 노선 버전 이력`}
          </div>
        }
        open={versionsModalVisible}
        onCancel={() => {
          setVersionsModalVisible(false);
          setVersioningPlaylist(null);
          setPlaylistVersions([]);
        }}
        footer={null}
        width={800}
      >
        <Table
          columns={[
            {
              title: '버전',
              dataIndex: 'version',
              key: 'version',
              width: 80,
              render: (version: number, record: PlaylistVersion) => (
                <div>
                  <Text strong>v{version}</Text>
                  {record.is_active && (
                    <div><Tag color="success">활성</Tag></div>
                  )}
                </div>
              )
            },
            {
              title: '버전명',
              dataIndex: 'name',
              key: 'name',
              render: (name: string, record: PlaylistVersion) => (
                <div>
                  <Text strong>{name}</Text>
                  {record.description && (
                    <div><Text type="secondary">{record.description}</Text></div>
                  )}
                </div>
              )
            },
            {
              title: '미디어',
              key: 'media',
              width: 120,
              render: (record: PlaylistVersion) => (
                <div>
                  <Text>{record.media_count}개</Text>
                  <div>
                    <Text type="secondary">
                      {Math.floor(record.total_duration / 60)}분 {record.total_duration % 60}초
                    </Text>
                  </div>
                </div>
              )
            },
            {
              title: '생성일',
              dataIndex: 'created_at',
              key: 'created_at',
              width: 150,
              render: (date: string, record: PlaylistVersion) => (
                <div>
                  <Text>{new Date(date).toLocaleDateString()}</Text>
                  {record.created_by_name && (
                    <div><Text type="secondary">by {record.created_by_name}</Text></div>
                  )}
                </div>
              )
            },
            {
              title: '작업',
              key: 'actions',
              width: 100,
              render: (record: PlaylistVersion) => (
                <Space>
                  {!record.is_active && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleActivateVersion(record.version)}
                    >
                      활성화
                    </Button>
                  )}
                </Space>
              )
            }
          ]}
          dataSource={playlistVersions}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Modal>
    </div>
  );
}